import React, { useState, useEffect, useContext, useMemo } from "react";
import { FilterDropdown, useTable } from "@refinedev/antd";
import {
  Input,
  Table,
  theme,
  type TableColumnsType,
  Form,
  Grid,
  Flex,
  Typography,
  Select,
  Button,
  Space,
} from "antd";
import {
  CrudFilters,
  getDefaultFilter,
  HttpError,
  useGetIdentity,
} from "@refinedev/core";
import {
  MessageOutlined,
  PlusOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { LateralModal } from "@/components/modals/LateralModal";
import { PaginationTotal } from "@/components/pagination-total";
import { TFunction, i18n as I18nType } from "i18next";
import { useTranslation } from "react-i18next";
import { SmartMenuContext } from "@/config/context/smartMenuContext";
import { User } from "@/types/schema/schema.types";
import { useNavigate } from "react-router-dom";
import { useFormatLastUpdate } from "@/utilities";
import dayjs from "dayjs";
import { FILAS_POR_PAGINA, OPCIONES_POR_PAGINA } from "@/config/paginacion";

const { Title, Text } = Typography;
const { Search } = Input;

export const StoresTemplatesList: React.FC<{
  children?: React.ReactNode;
}> = ({ children }) => {
  const screens = Grid.useBreakpoint();
  const { data: dataLoguinUser } = useGetIdentity<User>();
  const { smartMenuName } = useContext(SmartMenuContext);

  // ID del seller desde el LocalStorage
  const sellerCode = encodeURIComponent(
    smartMenuName?.replace(/"/g, "").toLowerCase(),
  );

  const { token } = theme.useToken();
  const { t, i18n }: { t: TFunction; i18n: I18nType } = useTranslation();
  const formatLastUpdate = useFormatLastUpdate();

  const capitalize = (str: string) =>
    `${str.charAt(0).toUpperCase()}${str.slice(1)}`;

  const onSearch = (params: any) => {
    const filters: CrudFilters = [];
    const { name_search } = params;

    filters.push({
      field: "search",
      operator: "contains",
      value: name_search,
    });

    return filters;
  };

  const {
    tableQuery: { data, isLoading, isFetching, refetch },
    searchFormProps,
    tableProps,
    filters,
    setFilters,
  } = useTable<HttpError>({
    resource: `email-templates/store/${sellerCode}`,
    pagination: {
      pageSize: FILAS_POR_PAGINA,
    },
    onSearch: onSearch,
  });

  const isLoadingTable = !data || isFetching;

  const dataSource = useMemo(
    () => (isLoadingTable ? [] : data?.data || []),
    [isLoadingTable, data],
  );

  const [filteredOptions, setFilteredOptions] = useState<string[]>([]);

  const uniqueNames = useMemo(() => {
    const names = new Set<string>();
    dataSource?.forEach((record) => {
      if (record.name) {
        names.add(record.name);
      }
    });
    return Array.from(names);
  }, [dataSource]);

  const columns: TableColumnsType<any> = [
    {
      key: "name",
      title: t("products.show.tableName"),
      dataIndex: "name",
      defaultFilteredValue: getDefaultFilter("name", filters, "contains"),
      filterDropdown: ({ confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Select
            placeholder="Search by name"
            style={{ width: 220, marginBottom: 8, display: "block" }}
            showSearch
            allowClear
            optionFilterProp="children"
            onSearch={(value) => {
              const filteredNames = uniqueNames
                .filter((name) =>
                  name.toLowerCase().includes(value.toLowerCase()),
                )
                .sort((a, b) => {
                  const countA = (a.match(new RegExp(value, "gi")) || [])
                    .length;
                  const countB = (b.match(new RegExp(value, "gi")) || [])
                    .length;
                  return countB - countA;
                });
              setFilteredOptions(filteredNames);
            }}
            onSelect={(value) => {
              setFilters([
                {
                  field: "name",
                  operator: "contains",
                  value: value,
                },
              ]);
              confirm(); // Confirma el filtro
            }}
            onClear={() => {
              setFilters([]); // Limpia los filtros
              confirm(); // Confirma la limpieza del filtro
            }}
            options={filteredOptions.map((name) => ({
              label: name,
              value: name,
            }))}
          />
          <Space>
            <Button
              type="primary"
              onClick={() => {
                clearFilters;
                confirm(); // Confirma el filtro
              }}
              size="small"
              style={{ width: 90 }}
            >
              Filter
            </Button>
            <Button
              onClick={() => {
                setFilters([]); // Limpia los filtros de la tabla
                confirm(); // Confirma la limpieza del filtro
              }}
              size="small"
              style={{ width: 90 }}
            >
              Clear
            </Button>
          </Space>
        </div>
      ),
      render: (name, record) => {
        return (
          <Flex gap={6} style={{ minWidth: 200 }}>
            <Text style={{ color: "rgba(43, 128, 88, 1)" }} strong>
              {name}
            </Text>
          </Flex>
        );
      },
    },
    {
      key: "subject",
      title: t("quotes.table.subject"),
      dataIndex: "subject",
      sorter: (a, b) =>
        a.user.email.toLowerCase().charCodeAt(0) -
        b.user.email.toLowerCase().charCodeAt(0),
      render: (subject, record) => {
        return (
          <Flex gap={6} style={{ minWidth: 200 }}>
            <Text>{subject}</Text>
          </Flex>
        );
      },
    },
    {
      key: "scope",
      title: t("quotes.table.type"),
      dataIndex: "scope",
      sorter: (a, b) => a.user.whatsapp - b.user.whatsapp,
      render: (scope, record) => {
        return (
          <Flex gap={7} style={{ minWidth: 200 }}>
            {capitalize(scope)}
          </Flex>
        );
      },
    },
    {
      key: "channel",
      title: t("quotes.label.templateChannel"),
      dataIndex: "channel",
      sorter: (a, b) =>
        a.channel.toLowerCase().charCodeAt(0) -
        b.channel.toLowerCase().charCodeAt(0),
      render: (_, record) => {
        return (
          <Flex gap={19} style={{ minWidth: 200 }}>
            {record.type}
            HTML email
          </Flex>
        );
      },
    },
    {
      key: "createdAt",
      title: t("quotes.table.created"),
      dataIndex: "createdAt",
      sorter: (a, b) =>
        a.createdAt.toLowerCase().charCodeAt(0) -
        b.createdAt.toLowerCase().charCodeAt(0),
      render: (createdAt, record) => {
        return (
          <Flex gap={19} style={{ minWidth: 200 }}>
            {dayjs(createdAt).format("D/M/YY")}
          </Flex>
        );
      },
    },
    {
      key: "updatedAt",
      title: t("quotes.table.updated"),
      dataIndex: "updatedAt",
      sorter: (a, b) =>
        a.updatedAt.toLowerCase().charCodeAt(0) -
        b.updatedAt.toLowerCase().charCodeAt(0),
      render: (updatedAt, record) => {
        return (
          <Flex gap={19} style={{ minWidth: 200 }}>
            {dayjs(updatedAt).format("D/M/YY")}
          </Flex>
        );
      },
    },
  ];

  const HeaderRow = React.memo(() => (
    <>
      <Flex flex="block" gap="large" wrap={screens.sm ? false : true}>
        <Form {...searchFormProps} style={{ width: "100%" }}>
          <Form.Item name="name_search">
            <Search
              placeholder={t("quotes.search.placeholder")}
              allowClear
              onSearch={(value) => {
                if (value.trim() === "") {
                  searchFormProps.form?.setFieldsValue({
                    name_search: undefined,
                  });
                }
                searchFormProps.form?.submit();
              }}
            />
          </Form.Item>
        </Form>

        <Flex
          gap="large"
          justify="center"
          style={{ width: screens.sm ? "auto" : "100%" }}
        >
          <Button
            iconPosition="end"
            icon={<PlusOutlined />}
            type="primary"
            onClick={() => navigate(`/stores/templates/${sellerCode}/new`)}
          >
            {t("quotes.label.titleNewTemplate")}
          </Button>
        </Flex>
      </Flex>
    </>
  ));

  const navigate = useNavigate();

  const handleRowClick = (record: any) => {
    navigate(`/stores/templates/${sellerCode}/${record?.templateId}`, {
      state: { record },
    });
  };

  const generateRowKey = (record: any) => {
    return `${record.templateId}`;
  };

  return (
    <>
      <Title level={3}>
        <Flex gap={8}>
          <MessageOutlined />
          Templates
        </Flex>
      </Title>

      <Table
        {...tableProps}
        style={{ cursor: "pointer" }}
        scroll={{ x: true }}
        title={() => <HeaderRow />}
        rowKey={(record) => generateRowKey(record)}
        onRow={(record) => ({
          onClick: () => {
            handleRowClick(record);
          },
        })}
        dataSource={dataSource}
        columns={columns}
        loading={isFetching}
        locale={{
          triggerAsc: t("table.triggerAsc"),
          triggerDesc: t("table.triggerDesc"),
          cancelSort: t("table.cancelSort"),
        }}
        pagination={{
          ...tableProps.pagination,
          showSizeChanger: true,
          pageSizeOptions: OPCIONES_POR_PAGINA,
          showQuickJumper: false,
          showTotal: (total) => <PaginationTotal total={total} />,
        }}
      />
    </>
  );
};
